// The 6 mock claims where the payer pays the patient (D197, H2 §1/§2, H3 §3).
// All Dr. Marcus Lee, MetLife or Guardian, out of network, no assignment of
// benefits. Five stay open ("With MetLife · pays the patient"). Iris Calder
// (CLM-28820) is open until Posting posts her, then closes as "Paid to the
// patient" (`IRIS_CLOSED`); historyMock swaps the two on `isPaidToPatientPosted`.
// Everything is dated on or before today (Oct 18, 2026).
import { PAYS_PATIENT_CLAIMS, type PaysPatientClaim } from '@/features/networks/paidToPatient';
import type { HistoryClaim, HistoryProcedure, TimelineEntry } from './historyMock';
import { PMS } from '@/features/shared/pms';

const PROVIDER = 'Dr. Marcus Lee';
const CONFIRMER = 'Ava Thompson (admin)';

export const IRIS_CLAIM_ID = 'CLM-28820';

interface Spec {
  visit: string;
  visitIso: string;
  memberId: string;
  plan: string;
  payerClaimNo: string;
  procedures: HistoryProcedure[];
  tooth: string | null;
}
const proc = (code: string, desc: string, tooth: string | null, fee: number): HistoryProcedure => ({ code, desc, tooth, fee });

const SPECS: Record<string, Spec> = {
  'CLM-28822': {
    visit: 'Oct 1', visitIso: '2026-10-01', memberId: '553-18-0442-01', plan: 'MetLife PDP', payerClaimNo: 'ME-60281934', tooth: '#30',
    procedures: [proc('D3330', 'Root canal, molar', '#30', 1150), proc('D2950', 'Core buildup', '#30', 300)],
  },
  'CLM-28820': {
    visit: 'Sep 30', visitIso: '2026-09-30', memberId: '553-27-1130-01', plan: 'MetLife PDP', payerClaimNo: 'ME-60284117', tooth: '#19',
    procedures: [proc('D2740', 'Crown, porcelain', '#19', 1150), proc('D2950', 'Core buildup', '#19', 300)],
  },
  'CLM-28818': {
    visit: 'Sep 25', visitIso: '2026-09-25', memberId: 'GU-4410-8821', plan: 'Guardian DPPO', payerClaimNo: 'GU-71920455', tooth: '#30',
    procedures: [proc('D2740', 'Crown, porcelain', '#30', 1150), proc('D2950', 'Core buildup', '#30', 300)],
  },
  'CLM-28812': {
    visit: 'Sep 21', visitIso: '2026-09-21', memberId: '553-61-0097-02', plan: 'MetLife PDP', payerClaimNo: 'ME-60276650', tooth: '#3',
    procedures: [proc('D3330', 'Root canal, molar', '#3', 1150), proc('D0220', 'Periapical X-ray', '#3', 30)],
  },
  'CLM-28806': {
    visit: 'Sep 17', visitIso: '2026-09-17', memberId: 'GU-5532-1907', plan: 'Guardian DPPO', payerClaimNo: 'GU-71903318', tooth: '#14',
    procedures: [proc('D2740', 'Crown, porcelain', '#14', 1295)],
  },
  'CLM-28798': {
    visit: 'Sep 9', visitIso: '2026-09-09', memberId: 'GU-2208-6643', plan: 'Guardian DPPO', payerClaimNo: 'GU-71881207', tooth: '#3',
    procedures: [proc('D2740', 'Crown, porcelain', '#3', 1295)],
  },
};

const tl = (stamp: string, iso: string, lead: string, who: string, detail?: string): TimelineEntry => ({
  stamp,
  iso,
  ok: true,
  lead,
  who,
  ...(detail ? { detail } : {}),
});

const SENT = `Sent it from ${PMS} · the clearinghouse accepted it`;

/** Prepared, Confirmed, Sent, Accepted: the days before the payer answers. */
function lifeUntilAccepted(p: PaysPatientClaim, s: Spec) {
  const sentShort = p.sent;
  const path = [
    { label: 'Prepared', date: s.visit },
    { label: 'Confirmed', date: sentShort },
    { label: 'Sent', date: sentShort },
    { label: 'Accepted', date: sentShort },
  ];
  const timeline: TimelineEntry[] = [
    tl(`${s.visit}, ${s.visit === sentShort ? '8:14 AM' : '5:42 PM'}`, s.visitIso, 'Prepared the claim', 'ELVA', `from ${PMS}’s draft`),
    tl(`${sentShort}, 9:05 AM`, p.sentIso, 'Confirmed the claim', CONFIRMER),
    tl(`${sentShort}, 9:06 AM`, p.sentIso, SENT, 'ELVA'),
    tl(`${sentShort}, 9:48 AM`, p.sentIso, `${p.payer} accepted the claim`, 'ELVA'),
  ];
  return { path, timeline };
}

function base(p: PaysPatientClaim): HistoryClaim {
  const s = SPECS[p.claimId];
  if (!s) throw new Error(`no spec for ${p.claimId}`);
  const first = p.patient.split(' ')[0] ?? p.patient;
  const life = lifeUntilAccepted(p, s);
  return {
    id: p.claimId,
    patient: p.patient,
    payer: p.payer,
    plan: s.plan,
    payerClaimNo: s.payerClaimNo,
    billed: p.billed,
    paid: null,
    allowed: null,
    patientOwes: 0,
    writeOff: null,
    visitIso: s.visitIso,
    visitShort: s.visit,
    visitFull: `${s.visit}, 2026`,
    lastChangeIso: p.sentIso,
    lastChangeLabel: p.sent,
    status: 'with-payer',
    tone: 'teal',
    where: `With ${p.payer} · pays the patient`,
    needsPerson: false,
    memberId: s.memberId,
    provider: PROVIDER,
    codes: [...new Set(s.procedures.map((x) => x.code))],
    procedures: s.procedures,
    path: life.path,
    nextStep: `Waiting for ${p.payer}`,
    nextNote: 'pays the patient',
    timeline: life.timeline,
    openIn: { label: 'Insurance A/R', to: '/ar', page: 'ar' },
    band: {
      pre: `${first}’s claim of ${s.visit} is with ${p.payer}. `,
      em: `${p.payer} pays the patient.`,
      mint: false,
    },
  };
}

/** The six claims as they stand before anything is posted. */
export const PAYS_PATIENT_OPEN: HistoryClaim[] = PAYS_PATIENT_CLAIMS.map(base);

/** Iris Calder after Posting posts her (H2 §2): closed as Paid to the patient. */
function buildIrisClosed(): HistoryClaim {
  const p = PAYS_PATIENT_CLAIMS.find((c) => c.claimId === IRIS_CLAIM_ID);
  if (!p) throw new Error('Iris Calder is missing from PAYS_PATIENT_CLAIMS');
  const open = base(p);
  const timeline: TimelineEntry[] = [
    ...open.timeline,
    tl('Oct 12, 7:20 AM', '2026-10-12', 'Read MetLife’s EOB: $580.00 paid to Iris, $0.00 to the practice', 'ELVA'),
    tl(
      'Oct 15, 10:12 AM',
      '2026-10-15',
      `Posted $0.00 in ${PMS} with the note “MetLife paid the patient” and closed the claim`,
      'confirmed by Ava Thompson (admin)',
    ),
  ];
  const { nextStep, nextNote, openIn, ...rest } = open;
  void nextStep;
  void nextNote;
  void openIn;
  return {
    ...rest,
    paid: 0,
    patientOwes: 950,
    status: 'paid-to-patient',
    tone: 'grey',
    where: 'Paid to the patient · $580.00 on Oct 12',
    lastChangeIso: '2026-10-15',
    lastChangeLabel: 'Oct 15',
    path: [
      ...open.path,
      { label: 'Paid to the patient', date: 'Oct 12' },
      { label: 'Closed', date: 'Oct 15' },
    ],
    timeline,
    payerPaidDate: 'Oct 12',
    closedDate: 'Oct 15',
    paidToPatient: { amount: 580, date: 'Oct 12', paidAtVisit: 500 },
    band: { pre: 'MetLife paid Iris directly for her visit of Sep 30. The claim is closed; Iris owes the practice $950.00.', mint: false },
  };
}

export const IRIS_CLOSED: HistoryClaim = buildIrisClosed();
